import { useNode } from '@craftjs/core';
import { css, cx, injectGlobal } from '@emotion/css';
import type { CSSInterpolation, CSSObject } from '@emotion/serialize';
import type { NodeCustomProps } from './../../types/craft';
import { useRenderContext } from '../../context/RenderContext';
import { getMediaQueryHeader } from '../../utils/styleHelpers';
import { useCountdownTimer, useCountdownLabelEditor } from './hooks';
import { formatNumber, getLabelText } from './utils';
import {
  COUNTDOWN_NODE_NAME,
  DEFAULT_EXPIRATION_EVENT,
  DEFAULT_INTERACTION_EVENT,
} from './../../utils/constants';
import {
  DEFAULT_VISIBILITY,
  getBoxShadow,
  getBorderRadiusStyle,
  getBorderStyles,
  getGlobalStyleColor,
  getGlobalTextColorStyle,
  getMarginStyle,
  getPaddingStyle,
  getPositionPropsV2,
  getPositionStyle,
  getVisibilityClasses,
} from './../../utils/styleHelpers';
import { getHrefAttr, getTargetAttr, getRelHrefAttr } from '../../utils/attributesHelpers';
import {
  getAnimationDataAttributes,
  getAnimationInitialClass,
  getHoverStyles,
} from '../../utils/animationHelpers';
import type { StylesConfig } from '../../types/styles';
import { CountdownPropsDto } from '../../dto/craft/props/CountdownPropsDto';

export type CountdownMode = 'standard' | 'daily' | 'evergreen';

export type CountdownProps = CountdownPropsDto;

// ============================================================================
// Default Props
// ============================================================================

// Default to 24 hours from now for a reasonable starting point
const getDefaultDateTime = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(12, 0, 0, 0);
  return tomorrow.toISOString();
};

export const defaultProps: CountdownProps = {
  positionAnchorsState: {
    enableTop: false,
    enableRight: false,
    enableBottom: false,
    enableLeft: false,
    enabled: false,
  },
  visibility: { ...DEFAULT_VISIBILITY },
  padding: [16, 24, 16, 24],
  margin: [0, 0, 0, 0],
  borderRadius: [8, 8, 8, 8],
  position: 'relative',
  top: '0px',
  left: '0px',
  right: '',
  bottom: '',
  borderWidth: 0,
  borderColor: undefined,
  mode: 'standard',
  dateTime: getDefaultDateTime(),
  evergreenId: `eg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  evergreenDuration: 604800, // Default 1 week in seconds
  type: 'default',

  showDays: true,
  showHours: true,
  showMinutes: true,
  showSeconds: true,

  // Default label texts
  labelDays: 'Days',
  labelHours: 'Hours',
  labelMinutes: 'Minutes',
  labelSeconds: 'Seconds',

  labelFontFamily: 'Inter',
  labelFontWeight: '400',

  numberFontFamily: 'Inter',
  numberFontWeight: '700',

  numberFontStyle: 'normal',
  labelFontStyle: 'normal',

  labelFontSize: '12px',
  numberFontSize: '32px',

  // Background Color
  backgroundColor: undefined,

  // Number Text Color
  numberTextColor: { r: 255, g: 255, b: 255, a: 1 },

  // Label Text Color
  labelTextColor: { r: 156, g: 163, b: 175, a: 1 },

  numberLetterSpacing: 0,
  labelLetterSpacing: 0.05,
  gap: 16,
  width: '100%',
  height: 'auto',
  minHeight: 64,

  // Flex properties
  fillSpace: 'yes',
  flexBasis: '100%',

  clickEvent: { ...DEFAULT_INTERACTION_EVENT },
  expireEvent: { ...DEFAULT_EXPIRATION_EVENT },
  tablet: {},
  mobile: {},
};

// ============================================================================
// Component
// ============================================================================

const Countdown = (inputProps: Partial<CountdownProps>) => {
  const { isEditable, environment, fontManager, styles } = useRenderContext();

  const props: CountdownProps = {
    ...defaultProps,
    ...inputProps,
  };

  const {
    visibility = DEFAULT_VISIBILITY,
    dateTime,
    mode,
    evergreenId,
    evergreenDuration,
    showDays,
    showHours,
    showMinutes,
    showSeconds,
    type,
    labelDays = 'Days',
    labelHours = 'Hours',
    labelMinutes = 'Minutes',
    labelSeconds = 'Seconds',
    numberFontFamily,
    numberFontWeight,
    numberFontStyle,
    labelFontFamily,
    labelFontWeight,
    labelFontStyle,
    gap,
    clickEvent = { ...DEFAULT_INTERACTION_EVENT },
    expireEvent = { ...DEFAULT_EXPIRATION_EVENT },
  } = props;

  const {
    isLocked,
    id,
    actions: { setProp },
  } = useNode((node) => ({
    isLocked: node.data.custom.isLocked,
    id: node.id,
  }));

  // Use countdown timer hook
  const timeRemaining = useCountdownTimer(
    dateTime,
    mode,
    showDays,
    showHours,
    showMinutes,
    showSeconds,
    evergreenDuration,
  );

  // Inject fonts
  const numberFontFaceRule = fontManager?.getFontFaceCss(
    numberFontFamily,
    numberFontWeight,
    numberFontStyle,
  );
  const labelFontFaceRule = fontManager?.getFontFaceCss(
    labelFontFamily,
    labelFontWeight,
    labelFontStyle,
  );
  if (numberFontFaceRule) injectGlobal(numberFontFaceRule);
  if (labelFontFaceRule) injectGlobal(labelFontFaceRule);

  const visibilityStyleClasses = getVisibilityClasses(visibility);
  const isServerRender = environment === 'server';
  const renderNodeWrapper = isServerRender || environment === 'inspiration-preview';

  // Click event handling
  const href = getHrefAttr(clickEvent);
  const target = getTargetAttr(clickEvent);
  const rel = getRelHrefAttr(clickEvent);
  const disablePointerEvents = isEditable && isLocked;

  // Use label editor hook
  const { editingLabel, labelRefs, handleLabelDoubleClick, handleLabelBlur, handleLabelKeyDown } =
    useCountdownLabelEditor(isEditable, isLocked, setProp);

  // Render a single time unit tile
  const renderTile = (
    value: number,
    unit: 'days' | 'hours' | 'minutes' | 'seconds',
    digits: number = 2,
  ) => {
    const label = getLabelText(unit, type, labelDays, labelHours, labelMinutes, labelSeconds);
    const isEditing = editingLabel === unit;

    return (
      <div className={tileStyle()} data-countdown-unit={unit}>
        <span className={numberStyle(props, styles)}>{formatNumber(value, digits)}</span>
        <span
          ref={(el) => {
            labelRefs.current[unit] = el;
          }}
          className={cx(labelStyle(props, styles), isEditable && !isLocked && 'cursor-text')}
          contentEditable={isEditing}
          suppressContentEditableWarning
          onDoubleClick={() => handleLabelDoubleClick(unit)}
          onBlur={(e) => handleLabelBlur(unit, e)}
          onKeyDown={(e) => handleLabelKeyDown(unit, e)}
        >
          {label}
        </span>
      </div>
    );
  };

  const isBuilderMode = environment === 'builder';

  const animationClass = getAnimationInitialClass(props.animationType, environment);
  const animationAttrs = getAnimationDataAttributes(props);

  const countdownElement = (
    <a
      className={cx(
        'countdown-content',
        isEditable && 'node-dashed-indicator boundary-indicator styled-node',
        countdownContainerStyle(props, isServerRender, styles, disablePointerEvents),
        animationClass,
      )}
      href={href}
      target={target}
      rel={rel}
      onClick={(e) => e.preventDefault()}
      {...animationAttrs}
      {...(isBuilderMode ? { 'data-animation-preview': id } : {})}
      data-countdown-id={id}
      data-countdown-target={dateTime}
      data-countdown-type={mode}
      data-evergreen-id={evergreenId}
      data-evergreen-duration={evergreenDuration}
      data-countdown-days={showDays ? '1' : '0'}
      data-countdown-hours={showHours ? '1' : '0'}
      data-countdown-minutes={showMinutes ? '1' : '0'}
      data-countdown-seconds={showSeconds ? '1' : '0'}
      data-expire-event-type={expireEvent?.eventType}
      data-expire-url={expireEvent?.eventData?.url || ''}
    >
      <div className={tilesContainerStyle(gap)} data-countdown-display>
        {showDays && <>{renderTile(timeRemaining.days, 'days', timeRemaining.days > 99 ? 3 : 2)}</>}
        {showHours && <>{renderTile(timeRemaining.hours, 'hours')}</>}
        {showMinutes && <>{renderTile(timeRemaining.minutes, 'minutes')}</>}
        {showSeconds && <>{renderTile(timeRemaining.seconds, 'seconds')}</>}
      </div>
    </a>
  );

  // Wrap with countdown-node div for server and inspiration-preview rendering
  // (in the editor, the Resizer component provides this wrapper instead)
  if (renderNodeWrapper) {
    return (
      <div
        className={cx(
          'countdown-node',
          visibilityStyleClasses,
          animationClass,
          countdownNodeStyle(props, isServerRender),
        )}
        {...animationAttrs}
      >
        {countdownElement}
      </div>
    );
  }

  return countdownElement;
};

// ============================================================================
// Craft.js Configuration
// ============================================================================

Countdown.craft = {
  displayName: COUNTDOWN_NODE_NAME,
  props: defaultProps,
  custom: {
    displayName: COUNTDOWN_NODE_NAME,
    isLocked: false,
  } as NodeCustomProps,
};

export default Countdown;

// ============================================================================
// Style Functions
// ============================================================================

export function countdownNodeStyle(props: Partial<CountdownProps>, isServerRendering: boolean) {
  const currentBpStyles = computeCountdownNodeStyles(props);
  const finalStyles: CSSInterpolation = {
    ...currentBpStyles,
  };
  const { mobile, tablet } = props;
  if (mobile) {
    const positionProps = getPositionPropsV2(mobile as Partial<CountdownProps>, props);
    const mobilePositionStyle = computeCountdownNodeStyles({
      ...mobile,
      ...positionProps,
    });
    finalStyles[getMediaQueryHeader(isServerRendering, 'mobile')] = mobilePositionStyle;
  }
  if (tablet) {
    const positionProps = getPositionPropsV2(tablet as Partial<CountdownProps>, props);
    const tabletPositionStyle = computeCountdownNodeStyles({
      ...tablet,
      ...positionProps,
    });
    finalStyles[getMediaQueryHeader(isServerRendering, 'tablet')] = tabletPositionStyle;
  }
  return css(finalStyles);
}

function computeCountdownNodeStyles(props: Partial<CountdownProps>) {
  const {
    position,
    top,
    left,
    right,
    bottom,
    positionAnchorsState,
    zIndex,
    width,
    minHeight,
    fillSpace,
    flexBasis,
    margin,
  } = props;
  const positionStyle = getPositionStyle(position, top, left, right, bottom, positionAnchorsState);
  const marginString = margin ? getMarginStyle(margin).margin : undefined;

  return {
    flex: fillSpace ? (fillSpace === 'yes' ? `1 1 ${flexBasis}` : 'unset') : undefined,
    padding: marginString,
    ...positionStyle,
    // Only set width if fillSpace is not 'yes' - let flex handle sizing when fillSpace is enabled
    width: fillSpace === 'yes' ? undefined : width || undefined,
    ...(minHeight && { minHeight: `${minHeight}px` }),
    zIndex: zIndex || undefined,
  };
}

// Export for use in EditorCountdown, returns plain object for inline styles
export function getCountdownNodeStylesObject(props: Partial<CountdownProps>) {
  const baseStyle = computeCountdownNodeStyles(props);
  const finalStyle = {
    ...baseStyle,
    display: 'flex',
    mobile: {},
    tablet: {},
  };

  const { mobile, tablet } = props;
  if (mobile) {
    const positionProps = getPositionPropsV2(mobile as Partial<CountdownProps>, props);
    const mobileStyles = computeCountdownNodeStyles({
      ...mobile,
      ...positionProps,
    } as Partial<CountdownProps>);
    finalStyle.mobile = mobileStyles;
  }
  if (tablet) {
    const positionProps = getPositionPropsV2(tablet as Partial<CountdownProps>, props);
    const tabletStyles = computeCountdownNodeStyles({
      ...tablet,
      ...positionProps,
    } as Partial<CountdownProps>);
    finalStyle.tablet = tabletStyles;
  }

  return finalStyle;
}

function countdownContainerStyle(
  props: Partial<CountdownProps>,
  isServerRendering: boolean,
  activeGlobalStyles: StylesConfig,
  disablePointerEvents: boolean,
) {
  const currentBpStyles = computeCountdownContainerStyles(props, activeGlobalStyles);

  const finalStyles: CSSInterpolation = {
    ...(currentBpStyles as CSSObject),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    textDecoration: 'none',
    pointerEvents: disablePointerEvents ? 'none' : 'auto',
    cursor: 'pointer',
    width: '100%',
    height: '100%',
  };

  const { mobile, tablet } = props;
  if (mobile) {
    const mobileStyles = computeCountdownContainerStyles(mobile, activeGlobalStyles);
    finalStyles[getMediaQueryHeader(isServerRendering, 'mobile')] = mobileStyles as CSSObject;
  }
  if (tablet) {
    const tabletStyles = computeCountdownContainerStyles(tablet, activeGlobalStyles);
    finalStyles[getMediaQueryHeader(isServerRendering, 'tablet')] = tabletStyles as CSSObject;
  }

  return css(finalStyles);
}

function computeCountdownContainerStyles(
  props: Partial<CountdownProps>,
  activeGlobalStyles: StylesConfig,
) {
  const {
    borderRadius,
    padding,
    margin,
    borderWidth,
    borderColor,
    backgroundColor,
    fieldShadowType,
    fieldShadowSettings,
    hoverEffect,
    hoverTransitionDuration,
    hoverIntensity,
  } = props;

  const paddingStyle = padding ? getPaddingStyle(padding) : {};
  const marginStyle = margin ? getMarginStyle(margin) : {};
  const borderStyles = borderWidth
    ? getBorderStyles(borderWidth, borderColor, activeGlobalStyles.colors)
    : {};
  const borderRadiusStyle = borderRadius ? getBorderRadiusStyle(borderRadius) : {};

  const backgroundStyle =
    backgroundColor !== undefined
      ? { background: getGlobalStyleColor(backgroundColor, activeGlobalStyles.colors) }
      : {};

  const hoverStyles = getHoverStyles(
    hoverEffect,
    hoverTransitionDuration,
    hoverIntensity,
    !!getBoxShadow(fieldShadowType, fieldShadowSettings),
  );

  return {
    ...borderRadiusStyle,
    ...paddingStyle,
    ...marginStyle,
    ...borderStyles,
    ...backgroundStyle,
    ...hoverStyles,
    boxShadow: getBoxShadow(fieldShadowType, fieldShadowSettings) ?? 'none',
  };
}

function tilesContainerStyle(gap: number) {
  return css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: `${gap}px`,
    width: '100%',
    flexWrap: 'wrap',
    overflow: 'hidden',
  });
}

function tileStyle() {
  return css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    minWidth: '50px',
  });
}

function numberStyle(props: Partial<CountdownProps>, activeGlobalStyles: StylesConfig) {
  const {
    numberFontFamily,
    numberFontWeight,
    numberFontStyle,
    numberFontSize,
    numberLetterSpacing,
    numberTextColor,
  } = props;

  const colorStyle = getGlobalTextColorStyle(numberTextColor, activeGlobalStyles.colors);

  return css({
    ...colorStyle,
    fontFamily: numberFontFamily,
    fontWeight: numberFontWeight,
    fontStyle: numberFontStyle,
    fontSize: numberFontSize,
    letterSpacing: numberLetterSpacing ? `${numberLetterSpacing}em` : undefined,
    lineHeight: 1,
    // Monospace rendering - use tabular nums for consistent width
    fontVariantNumeric: 'tabular-nums',
    fontFeatureSettings: '"tnum" on, "lnum" on',
  });
}

function labelStyle(props: Partial<CountdownProps>, activeGlobalStyles: StylesConfig) {
  const {
    labelFontFamily,
    labelFontWeight,
    labelFontStyle,
    labelFontSize,
    labelLetterSpacing,
    labelTextColor,
  } = props;

  const colorStyle = getGlobalTextColorStyle(labelTextColor, activeGlobalStyles.colors);

  return css({
    ...colorStyle,
    fontFamily: labelFontFamily,
    fontWeight: labelFontWeight,
    fontStyle: labelFontStyle,
    fontSize: labelFontSize,
    letterSpacing: labelLetterSpacing ? `${labelLetterSpacing}em` : undefined,
    lineHeight: 1,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  });
}
